/**
 * Board Columns Hooks (require ActiveBoardContext)
 */

import { useMemo, useCallback, useRef, useSyncExternalStore } from 'react';
import { useActiveBoardContext } from '../stores/ActiveBoardContext';
import type { ColumnDefinition } from '../../types';

export function useColumns(): ColumnDefinition[] {
  const { columnsStore } = useActiveBoardContext();
  const cachedRef = useRef<ColumnDefinition[]>([]);

  const subscribe = useCallback((onStoreChange: () => void) => {
    const subscription = columnsStore.subscribe(onStoreChange);
    return typeof subscription === 'function' ? subscription : subscription.unsubscribe;
  }, [columnsStore]);

  const getSnapshot = useCallback(() => {
    const state = columnsStore.getSnapshot();
    const newColumns = state.context.columns;

    if (cachedRef.current.length === newColumns.length &&
        cachedRef.current.every((c, i) => c.name === newColumns[i]?.name &&
          (c.defaultCategoryIds ?? []).join(',') === (newColumns[i]?.defaultCategoryIds ?? []).join(','))) {
      return cachedRef.current;
    }
    cachedRef.current = newColumns;
    return newColumns;
  }, [columnsStore]);

  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function useColumnNames(): string[] {
  const columns = useColumns();
  return useMemo(() => columns.map(c => c.name), [columns]);
}

export function useBoardStartDate(): string {
  return useActiveBoardContext().boardStartDate;
}

export function useColumnsActions() {
  const { columnsStore } = useActiveBoardContext();
  return useMemo(() => ({
    addColumn: (column: string, defaultCategoryIds?: string[]) =>
      columnsStore.send({ type: 'column.add', column, defaultCategoryIds }),
    updateColumn: (oldName: string, newName?: string, defaultCategoryIds?: string[]) =>
      columnsStore.send({ type: 'column.update', oldName, newName, defaultCategoryIds }),
    deleteColumn: (column: string) =>
      columnsStore.send({ type: 'column.delete', column }),
    reorderColumns: (columns: ColumnDefinition[]) =>
      columnsStore.send({ type: 'column.reorder', columns }),
    resetColumns: () =>
      columnsStore.send({ type: 'columns.reset' }),
    importColumns: (columns: string[] | ColumnDefinition[]) =>
      columnsStore.send({ type: 'columns.import', columns }),
  }), [columnsStore]);
}
